import { useEffect, useMemo, useState } from "react";
import { WrappedTick, cleanAxisLabel } from "@/components/radarLabels";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
} from "recharts";
import {
  UserRound, Search, ChevronRight, Activity, ShieldAlert, TrendingUp, Minus, Download, Users,
} from "lucide-react";

const ZONA_COLORS = { verde: "#16A34A", amarilla: "#E0A106", roja: "#DC2626" };
const ZONA_BG = { verde: "bg-[#E9F8EF]", amarilla: "bg-[#FFF7DA]", roja: "bg-[#FDECEC]" };
const ZONA_TEXT = { verde: "text-[#16A34A]", amarilla: "text-[#9C7A18]", roja: "text-[#DC2626]" };

const PERFIL_STYLES = {
  I: { bg: "bg-[#E9F8EF]", text: "text-[#16A34A]", label: "Perfil I" },
  II: { bg: "bg-[#EAF0FF]", text: "text-[#2C49A0]", label: "Perfil II" },
  III: { bg: "bg-[#FFF7DA]", text: "text-[#9C7A18]", label: "Perfil III" },
  IV: { bg: "bg-[#FDECEC]", text: "text-[#DC2626]", label: "Perfil IV" },
};

const CEP_LABELS = {
  motivacion: "Motivación", ansiedad: "Ansiedad", confianza: "Confianza",
  perseverancia: "Perseverancia", atencion: "Atención", frustracion: "Frustración",
};
const CAD_LABELS = {
  aritmetica: "Aritmética", algebra: "Álgebra", geometria: "Geometría",
  razonamiento: "Razonamiento", resolucion: "Resolución",
};

function MiniRadar({ cep = [], cad = [] }) {
  const cepGlobal = cep.length ? Math.round(cep.reduce((a, x) => a + (x.score || 0), 0) / cep.length) : null;
  const axes = [
    ...cad.map((x) => ({ key: CAD_LABELS[x.area] || cleanAxisLabel(x.area), academico: x.score, emocional: cepGlobal })),
    ...cep.map((x) => ({ key: CEP_LABELS[x.dim] || cleanAxisLabel(x.dim), emocional: x.score, academico: null })),
  ];
  if (!axes.length) return null;
  return (
    <div className="w-full h-[210px]" data-testid="mini-radar">
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={axes} outerRadius="56%">
          <PolarGrid stroke="#E6E8F0" />
          <PolarAngleAxis dataKey="key" tick={(p) => <WrappedTick {...p} fontSize={10} fill="#41496a" maxChars={11} />} />
          <Radar dataKey="emocional" stroke="#A43790" fill="#A43790" fillOpacity={0.3} connectNulls />
          <Radar dataKey="academico" stroke="#2C49A0" fill="#2C49A0" fillOpacity={0.4} connectNulls />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function IceBar({ value, zona }) {
  const color = ZONA_COLORS[zona] || "#6E7591";
  return (
    <div className="flex items-center gap-2 w-full" data-testid="ice-bar">
      <span className="text-xs font-extrabold tabular-nums" style={{ color, minWidth: 32 }}>
        {value != null ? value.toFixed(1) : "—"}
      </span>
      <div className="flex-1 h-2 rounded-full bg-[#F2F4FA] overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${Math.min(100, value || 0)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function AdminPerfilesEstudiantes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [diagData, setDiagData] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [zonaFilter, setZonaFilter] = useState("all");
  const [cohortMeta, setCohortMeta] = useState({ avg_ice: null });   // v4.3.0 · KPIs de cohorte (antes en «Diagnósticos Inteligentes»)
  const [exporting, setExporting] = useState(false);

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get("/diagnostics/cohort/excel", { responseType: "blob" });
      const match = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = match ? match[1] : "cohorte_diagnostica.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { alert("Error al exportar Excel"); } finally { setExporting(false); }
  };

  const prefix = user?.role === "docente" ? "/docente" : user?.role === "franquiciado" ? "/franquiciado" : "/admin";

  useEffect(() => {
    (async () => {
      try {
        const [studentsRes, diagRes] = await Promise.all([
          api.get("/students"),
          api.get("/diagnostics/dashboard").catch(() => ({ data: { students: [] } })),
        ]);
        setStudents(Array.isArray(studentsRes.data) ? studentsRes.data : []);
        // Index diagnostic data by student_id
        const map = {};
        for (const s of (diagRes.data?.students || [])) {
          map[s.student_id] = s;
        }
        setDiagData(map);
        setCohortMeta({ avg_ice: diagRes.data?.avg_ice ?? null });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    let list = students;
    const t = q.trim().toLowerCase();
    if (t) {
      list = list.filter((s) => (s.name || "").toLowerCase().includes(t) || (s.email || "").toLowerCase().includes(t));
    }
    if (zonaFilter !== "all") {
      if (zonaFilter === "sin") {
        list = list.filter((s) => !diagData[s.id]?.ice);
      } else {
        list = list.filter((s) => diagData[s.id]?.zona === zonaFilter);
      }
    }
    return list;
  }, [students, q, zonaFilter, diagData]);

  // Count per zone
  const zonaCounts = useMemo(() => {
    const c = { all: students.length, verde: 0, amarilla: 0, roja: 0, sin: 0 };
    students.forEach((s) => {
      const d = diagData[s.id];
      if (!d?.ice) c.sin++;
      else if (d.zona === "verde") c.verde++;
      else if (d.zona === "amarilla") c.amarilla++;
      else if (d.zona === "roja") c.roja++;
    });
    return c;
  }, [students, diagData]);

  // Count diagnosed
  const diagCount = Object.keys(diagData).length;

  return (
    <div className="space-y-6" data-testid="perfiles-estudiantes-page">
      <div>
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#A43790]">Perfil afectivo-cognitivo</p>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-[#1F2547] flex items-center gap-2">
          <UserRound className="text-[#A43790]" /> Perfiles y diagnóstico
        </h1>
        <p className="text-sm text-[#6E7591] mt-1">
          Vista integrada del estado emocional y académico de cada estudiante.
          {diagCount > 0 && <span className="ml-2 font-bold text-[#A43790]">{diagCount} con diagnóstico inteligente</span>}
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3" data-testid="perfiles-kpis">
        <div className="jasq-card p-4">
          <p className="text-[11px] font-extrabold uppercase text-[#6E7591] flex items-center gap-1"><Users size={12} /> Alumnos diagnosticados</p>
          <p className="text-3xl font-black text-[#1F2547]">{diagCount}<span className="text-sm text-[#6E7591]"> / {students.length}</span></p>
        </div>
        <div className="jasq-card p-4">
          <p className="text-[11px] font-extrabold uppercase text-[#6E7591] flex items-center gap-1"><Activity size={12} /> ICE promedio</p>
          <p className="text-3xl font-black text-[#A43790]">{cohortMeta.avg_ice ?? "—"}<span className="text-sm text-[#6E7591]">/100</span></p>
        </div>
        <div className="jasq-card p-4">
          <p className="text-[11px] font-extrabold uppercase text-[#6E7591] mb-1">Distribución por zona</p>
          <div className="flex gap-3 text-sm font-bold text-[#1F2547]">
            {["verde", "amarilla", "roja"].map((z) => (
              <span key={z} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full" style={{ background: ZONA_COLORS[z] }} />{zonaCounts[z] || 0}</span>
            ))}
          </div>
        </div>
        <div className="jasq-card p-4 flex items-center justify-center">
          <button onClick={handleExportExcel} disabled={exporting || diagCount === 0} data-testid="export-cohort-excel-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1F2547] text-white text-sm font-bold hover:bg-[#2C49A0] disabled:opacity-40">
            <Download size={15} /> {exporting ? "Exportando…" : "Exportar Excel"}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6ACC1]" />
          <input
            data-testid="perfiles-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre o correo…"
            className="w-full pl-9 pr-3 py-2.5 rounded-2xl border-2 border-[#E1E5F0] text-sm focus:border-[#A43790] outline-none"
          />
        </div>
        <div className="flex gap-1.5 flex-wrap" data-testid="zona-filters">
          {[
            { key: "all", label: "Todos", color: "#6E7591", bg: "#F2F4FA" },
            { key: "roja", label: "Roja", color: "#DC2626", bg: "#FDECEC" },
            { key: "amarilla", label: "Amarilla", color: "#9C7A18", bg: "#FFF7DA" },
            { key: "verde", label: "Verde", color: "#16A34A", bg: "#E9F8EF" },
            { key: "sin", label: "Sin Dx", color: "#6E7591", bg: "#F2F4FA" },
          ].map((z) => (
            <button
              key={z.key}
              onClick={() => setZonaFilter(z.key)}
              data-testid={`filter-${z.key}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full transition-all ${
                zonaFilter === z.key
                  ? "ring-2 ring-offset-1 shadow-sm"
                  : "opacity-70 hover:opacity-100"
              }`}
              style={{
                color: z.color,
                backgroundColor: z.bg,
                ...(zonaFilter === z.key ? { ringColor: z.color } : {}),
              }}
            >
              {z.label} ({zonaCounts[z.key] || 0})
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]">Cargando perfiles…</div>
      ) : filtered.length === 0 ? (
        <div className="jasq-card p-10 text-center text-sm text-[#6E7591]" data-testid="perfiles-empty">No hay estudiantes para mostrar.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="perfiles-grid">
          {filtered.map((s) => {
            const diag = diagData[s.id] || {};
            const hasDiag = !!diag.ice;
            const zona = diag.zona;
            const perfil = diag.perfil;
            const perfilStyle = PERFIL_STYLES[perfil] || null;
            const radar = diag.radar || {};

            return (
              <button
                key={s.id}
                onClick={() => navigate(`${prefix}/estudiante/${s.id}`)}
                data-testid={`perfil-card-${s.id}`}
                className="jasq-card p-4 text-left hover:border-[#A43790] hover:shadow-lg transition-all group relative overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center gap-3">
                  <img src={s.avatar || "/avatars/owl-classic.png"} alt="" className="w-11 h-11 rounded-full object-cover bg-[#F2F4FA] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-extrabold text-[#1F2547] truncate text-sm">{s.name}</p>
                    <p className="text-xs text-[#6E7591] truncate">{s.grade || s.email}</p>
                  </div>
                  <ChevronRight size={16} className="text-[#A6ACC1] group-hover:text-[#A43790] shrink-0" />
                </div>

                {/* Badges: Zona + Perfil */}
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  {hasDiag && zona && (
                    <span
                      data-testid={`zona-badge-${s.id}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ZONA_BG[zona] || "bg-[#F2F4FA]"} ${ZONA_TEXT[zona] || "text-[#6E7591]"}`}
                    >
                      {zona.charAt(0).toUpperCase() + zona.slice(1)}
                    </span>
                  )}
                  {perfilStyle && (
                    <span
                      data-testid={`perfil-badge-${s.id}`}
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${perfilStyle.bg} ${perfilStyle.text}`}
                    >
                      {perfilStyle.label}
                    </span>
                  )}
                  {!hasDiag && (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F2F4FA] text-[#6E7591]">
                      Sin diagnóstico
                    </span>
                  )}
                  {hasDiag && diag.discrepancy_index != null && Math.abs(diag.discrepancy_index) > 1.0 && (
                    <span
                      data-testid={`discrepancy-badge-${s.id}`}
                      className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFE9F0] text-[#A43790] inline-flex items-center gap-0.5"
                    >
                      <ShieldAlert size={10} /> Brecha
                    </span>
                  )}
                </div>

                {/* ICE Bar + Mini Radar */}
                {hasDiag ? (
                  <div className="mt-3">
                    <div className="space-y-1">
                      <p className="text-[10px] font-bold text-[#6E7591] uppercase tracking-wider flex items-center gap-1">
                        <Activity size={10} /> ICE
                      </p>
                      <IceBar value={diag.ice} zona={zona} />
                      {diag.perfil_label && (
                        <p className="text-[10px] text-[#4C5478] truncate">{diag.perfil_label}</p>
                      )}
                    </div>
                    <MiniRadar cep={radar.cep} cad={radar.cad} />
                  </div>
                ) : (
                  <div className="mt-3 pt-3 border-t border-[#F2F4FA]">
                    <p className="text-xs text-[#A6ACC1] italic text-center">
                      Completa CEP + CAD para activar el diagnóstico
                    </p>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
